/**
 * Currency → flag (US, EU, …) and display helpers for wallet / cash-in / cash-out rows.
 */

const CURRENCY_CODE_TO_FLAG = {
  USD: "US",
  EUR: "EU",
  GBP: "GB",
  JPY: "JP",
  AUD: "AU",
  CAD: "CA",
  CHF: "CH",
  CNY: "CN",
  INR: "IN",
  BRL: "BR",
  LBP: "LB",
  RUB: "RU",
  KRW: "KR",
  MXN: "MX",
  SGD: "SG",
  HKD: "HK",
  NZD: "NZ",
  SEK: "SE",
  NOK: "NO",
  DKK: "DK",
  PLN: "PL",
  TRY: "TR",
  ZAR: "ZA",
  AED: "AE",
  SAR: "SA",
  EGP: "EG",
  ILS: "IL",
  THB: "TH",
  MYR: "MY",
  IDR: "ID",
  PHP: "PH",
  VND: "VN",
  USDT: "US",
};

const SYMBOL_TO_FLAG = {
  "$": "US",
  "\uFF04": "US",
  "\u20ac": "EU",
  EUR: "EU",
  "\u00a3": "GB",
  "\u00a5": "JP",
};

function norm2LetterRegion(value) {
  if (value == null || value === "") return null;
  const s = String(value).trim();
  if (s.length === 2 && /^[a-zA-Z]{2}$/.test(s)) {
    const u = s.toUpperCase();
    return u === "UK" ? "GB" : u;
  }
  return null;
}

function isLikelyIso4217Code(value) {
  const t = String(value ?? "").trim();
  return /^[A-Za-z]{3}$/.test(t);
}

/** Walk nested API shapes (some responses tuck currency under extra levels). */
function walkCurrencyHints(obj, candidates, seen, depth) {
  if (depth > 6 || obj == null || typeof obj !== "object") return;
  if (seen.has(obj)) return;
  seen.add(obj);
  if (!Array.isArray(obj)) {
    const s = shallowCurrencyFromObject(obj);
    if (s.code || s.sym || s.country) candidates.push(s);
  }
  if (Array.isArray(obj)) {
    for (const el of obj) walkCurrencyHints(el, candidates, seen, depth + 1);
    return;
  }
  for (const k of Object.keys(obj)) {
    const v = obj[k];
    if (v == null || typeof v !== "object") continue;
    const kl = k.toLowerCase();
    if (
      kl.includes("currency") ||
      kl === "currencies" ||
      kl === "wallet" ||
      kl === "account"
    ) {
      walkCurrencyHints(v, candidates, seen, depth + 1);
    }
  }
}

/** Read code / symbol / country from a single flat or nested currency object */
function shallowCurrencyFromObject(o) {
  if (!o || typeof o !== "object") return { code: "", sym: "", country: "" };
  const rawCode =
    o.CURRENCY_CODE ||
    o.currency_code ||
    o.currencyCode ||
    (typeof o.CODE === "string" ? o.CODE : null) ||
    (typeof o.code === "string" ? o.code : null) ||
    "";
  let code = String(rawCode).trim();
  if (code && !isLikelyIso4217Code(code)) {
    code = "";
  } else if (code) {
    code = code.toUpperCase();
  }
  const sym = String(
    o.CURRENCY_SYMBOL ||
      o.currency_symbol ||
      o.currencySymbol ||
      o.PLATFORM_SYMBOL ||
      o.platform_symbol ||
      o.platformSymbol ||
      (typeof o.SYMBOL === "string" ? o.SYMBOL : "") ||
      (typeof o.symbol === "string" ? o.symbol : "") ||
      ""
  ).trim();
  const country = String(o.COUNTRY || o.country || "").trim();
  return { code, sym, country };
}

/** Collect currency hints from wallet row and common nested shapes (API varies). */
function collectCurrencyCandidates(wallet) {
  const candidates = [];
  const push = (o) => {
    const s = shallowCurrencyFromObject(o);
    if (s.code || s.sym || s.country) candidates.push(s);
  };

  if (!wallet || typeof wallet !== "object") return candidates;

  if (typeof wallet.currencies === "string" && isLikelyIso4217Code(wallet.currencies)) {
    candidates.push({
      code: wallet.currencies.trim().toUpperCase(),
      sym: "",
      country: "",
    });
  }
  if (typeof wallet.currency === "string" && isLikelyIso4217Code(wallet.currency)) {
    candidates.push({
      code: wallet.currency.trim().toUpperCase(),
      sym: "",
      country: "",
    });
  }

  push(wallet);
  push(wallet.currencies);
  push(wallet.currency);
  push(wallet.Currencies);
  push(wallet.Currency);

  if (Array.isArray(wallet.currencies)) {
    wallet.currencies.forEach((c) => push(c));
  }

  const acc = wallet.account || wallet.Account;
  if (acc && typeof acc === "object") {
    push(acc);
    push(acc.currencies);
    push(acc.currency);
    if (Array.isArray(acc.wallet)) {
      acc.wallet.forEach((w) => {
        push(w);
        push(w?.currencies);
        push(w?.currency);
      });
    }
  }

  const deep = [];
  walkCurrencyHints(wallet, deep, new WeakSet(), 0);
  candidates.push(...deep);

  return candidates;
}

export function pickCurrencyFields(source) {
  const candidates = collectCurrencyCandidates(source);
  let CODE = "";
  let SYM = "";
  let COUNTRY = "";
  for (const c of candidates) {
    if (!CODE && c.code) CODE = c.code;
    if (!SYM && c.sym) SYM = c.sym;
    if (!COUNTRY && c.country) COUNTRY = c.country;
  }
  return {
    CODE: CODE,
    SYM: SYM,
    COUNTRY: COUNTRY,
  };
}

export function getFlagCountryCodeFromParts(currencyCode, currencySymbol = "", country = "") {
  const fromCountry = norm2LetterRegion(country);
  if (fromCountry) return fromCountry;

  const code = String(currencyCode || "")
    .trim()
    .toUpperCase();
  if (code && CURRENCY_CODE_TO_FLAG[code]) return CURRENCY_CODE_TO_FLAG[code];

  const sym = String(currencySymbol || "").trim();
  if (sym && SYMBOL_TO_FLAG[sym]) return SYMBOL_TO_FLAG[sym];

  return null;
}

export function getFlagCountryCodeForWallet(walletOrAccountRow) {
  const { CODE, SYM, COUNTRY } = pickCurrencyFields(walletOrAccountRow || {});
  return getFlagCountryCodeFromParts(CODE, SYM, COUNTRY);
}

/** If row has no code, try last ISO-like token in dropdown label (e.g. "100.00 USD"). */
export function getFlagCountryCodeForCashWalletOption(walletOrAccountRow, optionLabel) {
  return resolveCashWalletCurrencyDisplay(walletOrAccountRow, optionLabel || "").flagCode;
}

/**
 * Single source of truth for cash-in / cash-out wallet line: code, symbol, flag, label suffix.
 */
export function resolveCashWalletCurrencyDisplay(walletRow, fallbackLabel = "") {
  let { CODE: code, SYM: sym, COUNTRY: country } = pickCurrencyFields(walletRow || {});

  if (!code && !sym && fallbackLabel) {
    const parts = String(fallbackLabel).trim().split(/\s+/).filter(Boolean);
    for (let i = parts.length - 1; i >= 0; i--) {
      const p = parts[i];
      if (p && /^[A-Za-z]{3}$/.test(p)) {
        code = p.toUpperCase();
        break;
      }
    }
  }

  const flagCode = getFlagCountryCodeFromParts(code, sym, country);
  const suffix = (code || sym || "").trim();
  return {
    code,
    sym,
    country,
    flagCode,
    suffix,
  };
}

/** Dropdown option label: "1234.56 USD" (or balance only if currency unknown). */
export function formatCashWalletDropdownLabel(walletRow, balanceRaw) {
  const balanceNum =
    typeof balanceRaw === "string"
      ? parseFloat(balanceRaw.replace(/,/g, ""))
      : parseFloat(balanceRaw);
  const formattedBalance = Number.isNaN(balanceNum) ? 0 : balanceNum;
  const { suffix } = resolveCashWalletCurrencyDisplay(walletRow, "");
  const bal = formattedBalance.toFixed(2);
  return suffix ? `${bal} ${suffix}` : bal;
}
